import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Slider } from "@/components/ui/slider";
import { Atom, Play, Sparkle, Plus, X } from "@phosphor-icons/react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ReferenceArea, ResponsiveContainer, CartesianGrid } from "recharts";

const COLORS = ["#4dabf5", "#00e676", "#ffb340"];
const INTENS = [["light", "Лёгкие"], ["mid", "Средние"], ["heavy", "Тяжёлые"]];
const fmtBed = (b) => `${String(Math.floor(b % 24)).padStart(2, "0")}:${String(Math.round((b % 1) * 60)).padStart(2, "0")}`;
const zoneColor = (v) => (v >= 67 ? "#00e676" : v >= 34 ? "#ffea00" : "#ff3b30");

const StrategyEditor = ({ strat, idx, onChange, onRemove, canRemove, nutrition, labs }) => (
  <div className="glass-card p-4" data-testid={`twin-strategy-${idx}`} style={{ borderTop: `2px solid ${COLORS[idx]}` }}>
    <div className="flex items-center gap-2 mb-3">
      <input value={strat.name} onChange={(e) => onChange({ ...strat, name: e.target.value })}
        data-testid={`twin-strat-name-${idx}`}
        className="bg-transparent font-semibold text-sm outline-none flex-1 min-w-0" style={{ color: COLORS[idx] }} />
      {canRemove && (
        <button onClick={onRemove} data-testid={`twin-strat-remove-${idx}`} className="text-muted-foreground hover:text-red-400"><X size={15} /></button>
      )}
    </div>
    <div className="space-y-3.5">
      <div>
        <div className="flex justify-between text-xs mb-1.5 text-muted-foreground">
          <span>Тренировок в неделю</span><b className="num text-foreground">{strat.workouts_per_week}</b>
        </div>
        <Slider value={[strat.workouts_per_week]} min={0} max={7} step={1} data-testid={`twin-wpw-${idx}`}
          onValueChange={(v) => onChange({ ...strat, workouts_per_week: v[0] })} />
      </div>
      <div>
        <div className="text-xs text-muted-foreground mb-1.5">Интенсивность</div>
        <div className="flex gap-1">
          {INTENS.map(([id, label]) => (
            <button key={id} onClick={() => onChange({ ...strat, intensity: id })} data-testid={`twin-intensity-${idx}-${id}`}
              className={`flex-1 py-1 rounded-md text-[11px] font-semibold transition-colors ${strat.intensity === id ? "bg-primary text-white" : "bg-white/[0.05] text-muted-foreground"}`}>
              {label}
            </button>
          ))}
        </div>
      </div>
      <div>
        <div className="flex justify-between text-xs mb-1.5 text-muted-foreground">
          <span>Отбой</span><b className="num text-foreground">{fmtBed(strat.bed_h)}</b>
        </div>
        <Slider value={[strat.bed_h]} min={21.5} max={26} step={0.25} data-testid={`twin-bed-${idx}`}
          onValueChange={(v) => onChange({ ...strat, bed_h: v[0] })} />
      </div>
      <div>
        <div className="flex justify-between text-xs mb-1.5 text-muted-foreground">
          <span>Сон</span><b className="num text-foreground">{strat.sleep_h} ч</b>
        </div>
        <Slider value={[strat.sleep_h]} min={5} max={10} step={0.25} data-testid={`twin-sleep-${idx}`}
          onValueChange={(v) => onChange({ ...strat, sleep_h: v[0] })} />
      </div>
      {(nutrition?.available || labs?.available) && (
        <div className="pt-1 space-y-1.5 border-t border-white/[0.06]">
          {nutrition?.available && nutrition.late_delta != null && (
            <button onClick={() => onChange({ ...strat, late_eating: !strat.late_eating })} data-testid={`twin-late-${idx}`}
              className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-md text-[11px] border transition-colors ${strat.late_eating ? "bg-[#ff9100]/15 border-[#ff9100]/35 text-[#ff9100]" : "bg-white/[0.04] border-white/[0.08] text-muted-foreground"}`}>
              <span>🌙 Ужин после 21:00</span>
              <b className="num">{strat.late_eating ? `${nutrition.late_delta > 0 ? "+" : ""}${nutrition.late_delta}%` : "выкл"}</b>
            </button>
          )}
          {nutrition?.available && nutrition.protein_delta != null && (
            <button onClick={() => onChange({ ...strat, protein_ok: !strat.protein_ok })} data-testid={`twin-protein-${idx}`}
              className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-md text-[11px] border transition-colors ${strat.protein_ok ? "bg-[#00e676]/10 border-[#00e676]/30 text-[#00e676]" : "bg-white/[0.04] border-white/[0.08] text-muted-foreground"}`}>
              <span>🥩 Белок по норме</span>
              <b className="num">{strat.protein_ok ? "да" : `−${Math.abs(nutrition.protein_delta)}%`}</b>
            </button>
          )}
          {labs?.available && (
            <button onClick={() => onChange({ ...strat, fix_deficits: !strat.fix_deficits })} data-testid={`twin-deficits-${idx}`}
              className={`w-full flex items-center justify-between px-2.5 py-1.5 rounded-md text-[11px] border transition-colors ${strat.fix_deficits ? "bg-[#e879f9]/15 border-[#e879f9]/40 text-[#e879f9]" : "bg-white/[0.04] border-white/[0.08] text-muted-foreground"}`}>
              <span>💊 Дефициты устранены</span>
              <b className="num">{strat.fix_deficits ? `+${labs.bonus}%` : "нет"}</b>
            </button>
          )}
        </div>
      )}
    </div>
  </div>
);

export default function TwinPage() {
  const [model, setModel] = useState();
  const [strategies, setStrategies] = useState(null);
  const [results, setResults] = useState(null);
  const [quality, setQuality] = useState(null);
  const [optimum, setOptimum] = useState(null);
  const [loading, setLoading] = useState(false);
  const [optLoading, setOptLoading] = useState(false);

  useEffect(() => {
    api.get("/twin/model").then(({ data }) => {
      setModel(data);
      if (data.model) {
        const cur = { ...data.model.current, late_eating: false, protein_ok: true, fix_deficits: false };
        setStrategies([
          { name: "Как сейчас", ...cur },
          { name: "Эксперимент", ...cur, workouts_per_week: Math.min(cur.workouts_per_week + 1, 7), sleep_h: Math.min(cur.sleep_h + 0.5, 10) },
        ]);
      }
    }).catch(() => setModel({ model: null, reason: "error" }));
  }, []);

  const run = async (strats = strategies) => {
    setLoading(true);
    try {
      const { data } = await api.post("/twin/simulate", { strategies: strats });
      setResults(data.results);
      setQuality(data.model_quality);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const findOptimum = async () => {
    setOptLoading(true);
    try {
      const { data } = await api.get("/twin/optimize");
      setOptimum(data.top);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setOptLoading(false);
    }
  };

  const chartData = results
    ? results[0].series.map((_, i) => {
        const row = { day: i + 1 };
        results.forEach((r, ri) => { row[`s${ri}`] = r.series[i].recovery; });
        return row;
      })
    : null;

  if (model === undefined) return <div className="glass-card h-40 animate-pulse mt-10" />;

  return (
    <div className="fade-in" data-testid="twin-page">
      <div className="mb-6">
        <p className="text-xs uppercase tracking-widest text-muted-foreground mb-1 flex items-center gap-1.5"><Atom size={14} weight="fill" className="text-primary" />Симуляция организма</p>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight">Цифровой двойник</h1>
        <p className="text-sm text-muted-foreground mt-2 max-w-2xl">
          Модель, обученная на всей вашей истории Whoop, проигрывает следующие 30 дней при разных стратегиях жизни — с накоплением усталости (ACWR) и долга сна.
        </p>
      </div>

      {!model.model ? (
        <div className="glass-card p-8 text-center" data-testid="twin-no-data">
          <Atom size={40} className="text-muted-foreground mx-auto mb-3" />
          <p className="font-semibold mb-1">{model.reason === "no_whoop" ? "Подключите Whoop" : "Недостаточно данных"}</p>
          <p className="text-sm text-muted-foreground">Двойнику нужно минимум 20 дней истории Whoop, чтобы обучить персональную модель.</p>
        </div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4 items-start">
            {strategies.map((s, i) => (
              <StrategyEditor key={i} strat={s} idx={i} canRemove={strategies.length > 1} nutrition={model.model.nutrition} labs={model.model.labs}
                onChange={(ns) => setStrategies(strategies.map((x, xi) => (xi === i ? ns : x)))}
                onRemove={() => { setStrategies(strategies.filter((_, xi) => xi !== i)); setResults(null); }} />
            ))}
            {strategies.length < 3 && (
              <button onClick={() => setStrategies([...strategies, { name: `Стратегия ${strategies.length + 1}`, ...model.model.current, late_eating: false, protein_ok: true }])}
                data-testid="twin-add-strategy"
                className="glass-card p-4 flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground min-h-[100px] transition-colors">
                <Plus size={17} /> Добавить стратегию
              </button>
            )}
          </div>

          <div className="flex flex-wrap items-center gap-3 mb-2">
            <button onClick={() => run()} disabled={loading} data-testid="twin-run-btn"
              className="flex items-center gap-2 px-5 py-2.5 rounded-full bg-primary text-white text-sm font-bold disabled:opacity-50 hover:brightness-110 transition-all">
              <Play size={16} weight="fill" /> {loading ? "Симуляция..." : "Запустить симуляцию 30 дней"}
            </button>
            <button onClick={findOptimum} disabled={optLoading} data-testid="twin-optimize-btn"
              className="flex items-center gap-2 px-4 py-2.5 rounded-full glass-panel border border-[#a970ff]/30 text-sm font-semibold text-[#a970ff] disabled:opacity-50 hover:bg-[#a970ff]/10 transition-colors">
              <Sparkle size={16} weight="fill" /> {optLoading ? "Перебираю 192 сценария..." : "Найти оптимум"}
            </button>
          </div>
          {model.model.nutrition && !model.model.nutrition.available && (
            <p className="text-[10px] text-muted-foreground mb-4" data-testid="twin-nutrition-hint">
              🍽 Ведите дневник «Питание» хотя бы 5 дней — двойник начнёт учитывать влияние еды на прогноз.
            </p>
          )}
          {model.model.nutrition?.available && <div className="mb-4" />}

          {model.model.labs?.available && (
            <p className="text-[10px] text-muted-foreground mb-4" data-testid="twin-labs-hint">
              💊 По вашим анализам найдены дефициты: {model.model.labs.deficits.map((d) => `${d.name} (${d.flag === "low" ? "↓" : "↑"})`).join(", ")}.
              Тумблер «Дефициты устранены» покажет оценку эффекта +{model.model.labs.bonus}% к восстановлению (литературная оценка, не гарантия).
            </p>
          )}

          {optimum && (
            <div className="glass-card p-5 mb-6" data-testid="twin-optimum">
              <h3 className="text-sm font-semibold mb-3 flex items-center gap-2"><Sparkle size={16} weight="fill" className="text-[#a970ff]" />Лучшие стратегии по перебору</h3>
              <div className="space-y-2">
                {optimum.map((o, i) => (
                  <button key={i} data-testid={`twin-opt-${i}`}
                    onClick={() => {
                      const ns = [...strategies];
                      ns[0] = { name: `Оптимум #${i + 1}`, ...o.strategy };
                      setStrategies(ns);
                      run(ns);
                    }}
                    className="w-full flex flex-wrap items-center gap-2 rounded-lg bg-white/[0.04] border border-white/[0.07] px-3 py-2.5 text-left hover:bg-white/[0.07] transition-colors">
                    <span className="font-heading font-bold num text-lg" style={{ color: zoneColor(o.summary.avg) }}>{o.summary.avg}%</span>
                    <span className="text-xs flex-1 min-w-[200px]">{o.label}</span>
                    <span className="text-[10px] text-muted-foreground num">зелёных: {o.summary.green} · красных: {o.summary.red}</span>
                  </button>
                ))}
              </div>
              <p className="text-[10px] text-muted-foreground mt-2">Нажмите на стратегию — она подставится первой и симуляция перезапустится.</p>
            </div>
          )}

          {results && (
            <>
              <div className="glass-card p-5 mb-4" data-testid="twin-chart">
                <h3 className="text-sm font-semibold mb-3">Прогноз восстановления на 30 дней</h3>
                <ResponsiveContainer width="100%" height={280}>
                  <LineChart data={chartData} margin={{ top: 5, right: 10, bottom: 0, left: -20 }}>
                    <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                    <ReferenceArea y1={67} y2={100} fill="#00e676" fillOpacity={0.05} />
                    <ReferenceArea y1={0} y2={34} fill="#ff3b30" fillOpacity={0.05} />
                    <XAxis dataKey="day" tick={{ fontSize: 10, fill: "#8b93a7" }} tickLine={false} axisLine={false} />
                    <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: "#8b93a7" }} tickLine={false} axisLine={false} />
                    <Tooltip contentStyle={{ background: "rgba(15,18,28,0.95)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }}
                      labelFormatter={(d) => `День ${d}`}
                      formatter={(v, name) => [`${v}%`, results[Number(name.slice(1))]?.name || name]} />
                    {results.map((r, i) => (
                      <Line key={i} dataKey={`s${i}`} stroke={COLORS[i]} strokeWidth={2.5} dot={false} type="monotone" />
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                {results.map((r, i) => (
                  <div key={i} className="glass-card p-4" data-testid={`twin-summary-${i}`} style={{ borderTop: `2px solid ${COLORS[i]}` }}>
                    <div className="text-xs font-semibold mb-2" style={{ color: COLORS[i] }}>{r.name}</div>
                    <div className="flex items-baseline gap-2 mb-2">
                      <span className="font-heading text-3xl font-bold num" style={{ color: zoneColor(r.summary.avg) }}>{r.summary.avg}%</span>
                      <span className="text-[10px] text-muted-foreground">среднее восстановление</span>
                    </div>
                    <div className="flex gap-3 text-[11px] num mb-2">
                      <span className="text-[#00e676]">🟢 {r.summary.green}</span>
                      <span className="text-[#ff3b30]">🔴 {r.summary.red}</span>
                      <span className="text-muted-foreground">мин. {r.summary.min}%</span>
                      <span className="text-muted-foreground">финиш {r.summary.end}%</span>
                    </div>
                    <p className="text-xs text-muted-foreground">{r.summary.verdict}</p>
                  </div>
                ))}
              </div>
              {quality && (
                <p className="text-[10px] text-muted-foreground" data-testid="twin-quality">
                  Модель обучена на {quality.n} ваших днях · точность ±{quality.rmse}% · R² {quality.r2}. Симуляция — оценка, а не гарантия.
                </p>
              )}
            </>
          )}
        </>
      )}
    </div>
  );
}
